import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { fmtDate, money, type PageMeta } from '../lib/types'
import { StatusBadge } from '../components/StatusBadge'

type Payment = {
  id: string
  inv_id: number
  user_name?: string | null
  sku: string
  description?: string | null
  amount: number | string
  currency: string
  status: string
  fulfillment_error?: string | null
  paid_at?: string | null
  created_at?: string | null
}

type Sub = {
  id: string
  user_id: string
  user_name?: string | null
  user_email?: string | null
  plan: string
  status: string
  expires_at?: string | null
}

export function PaymentsPage() {
  const [params, setParams] = useSearchParams()
  const status = params.get('status') || ''
  const page = Number(params.get('page') || 1)
  const qs = new URLSearchParams({ page: String(page) })
  if (status) qs.set('status', status)
  const { data, isLoading } = useQuery({
    queryKey: ['payments', qs.toString()],
    queryFn: () => api<{ items: Payment[]; meta: PageMeta }>(`/admin/payments?${qs}`),
  })

  return (
    <div>
      <h1 className="page-title">Продажи</h1>
      <p className="page-sub">Пакеты откликов, PRO и продвижение заказов. Возвраты делаются в кабинете Robokassa.</p>
      <div className="toolbar">
        <select className="select" value={status} onChange={(e) => {
          const next = new URLSearchParams(params)
          if (e.target.value) next.set('status', e.target.value); else next.delete('status')
          next.delete('page')
          setParams(next)
        }}>
          <option value="">Все статусы</option>
          {['pending', 'succeeded', 'failed', 'refunded'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Дата</th>
              <th>InvId</th>
              <th>Пользователь</th>
              <th>Покупка</th>
              <th>Сумма</th>
              <th>Статус</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6}>Загрузка…</td></tr>}
            {data?.items.map((p) => (
              <tr key={p.id}>
                <td>{fmtDate(p.paid_at || p.created_at)}</td>
                <td className="mono">{p.inv_id}</td>
                <td>{p.user_name || '—'}</td>
                <td>
                  {p.description || p.sku}
                  {p.fulfillment_error && (
                    <div className="muted" style={{ color: 'var(--danger, #c0392b)' }}>
                      Не выдано: {p.fulfillment_error} — нужен возврат
                    </div>
                  )}
                </td>
                <td>{money(p.amount, p.currency)}</td>
                <td><StatusBadge value={p.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function SubscriptionsPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['admin-subs'],
    queryFn: () => api<{ items: Sub[]; meta: PageMeta }>('/admin/subscriptions?page_size=100'),
  })
  const grant = useMutation({
    mutationFn: (userId: string) =>
      api(`/admin/subscriptions/${userId}/grant`, {
        method: 'POST',
        body: JSON.stringify({ plan: 'PRO', days: 30 }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-subs'] }),
  })
  const revoke = useMutation({
    mutationFn: (userId: string) =>
      api(`/admin/subscriptions/${userId}/revoke`, { method: 'POST', body: '{}' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-subs'] }),
  })

  return (
    <div>
      <h1 className="page-title">Подписки</h1>
      <p className="page-sub">Выдача / отзыв PRO (finance / super_admin)</p>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Пользователь</th>
              <th>План</th>
              <th>Статус</th>
              <th>Истекает</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5}>Загрузка…</td></tr>}
            {data?.items.map((s) => (
              <tr key={s.id}>
                <td>{s.user_name || s.user_email || s.user_id}</td>
                <td>{s.plan}</td>
                <td><StatusBadge value={s.status} /></td>
                <td>{fmtDate(s.expires_at)}</td>
                <td>
                  <div className="row">
                    <button className="btn btn-success" onClick={() => grant.mutate(s.user_id)}>PRO 30д</button>
                    <button className="btn btn-danger" onClick={() => revoke.mutate(s.user_id)}>Сбросить</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
